import {getElementSelector} from './get-element-selector';
import type {Moment} from 'moment-timezone';

/**
 * Stringify any value for logger system
 */
export function stringify(value: any): string {
  return _stringify(value);
}

function _stringify(value: any, depth: number = 0, seen: WeakSet<any> = new WeakSet()): string {
  try {
    // Handle primitive types
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string' && depth === 0) return value;
    if (typeof value === 'string') return `"${value.replace(/"/g, '\\"')}"`;
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') {
      if (Number.isNaN(value)) return 'NaN';
      if (value === Infinity) return 'Infinity';
      if (value === -Infinity) return '-Infinity';
      return String(value);
    }
    if (typeof value === 'bigint') return `${value}n`;
    if (typeof value === 'symbol') return value.toString();

    // Handle functions
    if (typeof value === 'function') {
      return `[Function: ${value.name || 'anonymousFunction'}]`;
    }

    // Prevent infinite recursion with circular references
    if (typeof value === 'object' && seen.has(value)) {
      return '[Circular reference]';
    }

    // Prevent excessive nesting
    if (depth > 10) {
      return `[Max Depth Reached]`;
    }

    if (typeof value === 'object') {
      seen.add(value);

      try {
        // Handle DOM elements
        if (window.Element && typeof window.Element === 'function' && value instanceof window.Element) {
          return `[DOM element: ${getElementSelector(value)}]`;
        }

        // Handle Events
        if (
          // ErrorEvent do not present on legacy platforms
          (window.ErrorEvent && typeof window.ErrorEvent === 'function' && value instanceof window.ErrorEvent) ||
          (window.Event && typeof window.Event === 'function' && value instanceof window.Event)
        ) {
          const {message, filename, lineno, colno, error} = value as ErrorEvent;
          return `[Error Event]: ${_stringify({message, filename, lineno, colno, error}, depth + 1, seen)}`;
        }

        // Handle Error objects
        if (window.Error && typeof window.Error === 'function' && value instanceof window.Error) {
          return `{\n${indent(depth + 1)}"name": "${value.name}",\n${indent(depth + 1)}"message": "${value.message}",\n${indent(depth + 1)}"stack": "${value.stack || ''}"\n${indent(depth)}}`;
        }

        // Handle Arrays
        if (Array.isArray(value)) {
          const items = value.map((item) => indent(depth + 1) + _stringify(item, depth + 1, seen)).join(',\n');
          return items.length === 0 ? '[]' : `[\n${items}\n${indent(depth)}]`;
        }

        // JQuery DOM elements
        // @ts-expect-error TS2339: Property jQuery does not exist on type Window & typeof globalThis
        if (window.jQuery && value instanceof window.jQuery) {
          const selectors = (value as JQuery<Element>)
            .toArray()
            .map((domElement) => getElementSelector(domElement))
            .join('; ');
          return `[JQuery element: ${selectors}]`;
        }

        // Moment.js objects
        if (window.moment && window.moment.isMoment(value)) {
          if (!value.isValid()) {
            return `[Invalid Moment Date]`;
          }
          return `"${(value as Moment).toISOString()}"`;
        }

        // Handle Date objects
        if (value instanceof Date) {
          return `"${value.toISOString()}"`;
        }

        // Handle RegExp objects
        if (window.RegExp && typeof RegExp === 'function' && value instanceof RegExp) {
          return value.toString();
        }

        // Handle Sets
        if (window.Set && typeof window.Set === 'function' && value instanceof window.Set) {
          const items = Array.from(value).map((item) => _stringify(item, depth + 1, seen));
          return `Set{${items.join(', ')}}`;
        }

        // Handle Maps
        if (window.Map && typeof window.Map === 'function' && value instanceof window.Map) {
          const entries = Array.from(value.entries()).map(
            ([k, v]) => `${_stringify(k, depth + 1, seen)}: ${_stringify(v, depth + 1, seen)}`,
          );
          return `Map{${entries.join(', ')}}`;
        }

        // Handle plain objects
        const pairs: string[] = [];
        for (const [key, val] of Object.entries(value)) {
          const serializedKey = typeof key === 'string' ? `"${key}"` : _stringify(key, depth + 1, seen);
          pairs.push(`${indent(depth + 1)}${serializedKey}: ${_stringify(val, depth + 1, seen)}`);
        }
        const pairsStr = pairs.join(',\n');

        return pairsStr.length > 0 ? `{\n${pairsStr}\n${indent(depth)}}` : '{}';
      } finally {
        seen.delete(value);
      }
    }

    return String(value);
  } catch (error) {
    let errMsg;
    if (window.Error && typeof window.Error === 'function' && error instanceof window.Error) {
      errMsg = `${error.name} ${error.message}` || 'Unknown error';
    } else if (error?.toString) {
      errMsg = error.toString();
    } else {
      errMsg = 'Unknown error';
    }
    return `[Serialization Error: ${errMsg}]`;
  }
}

function indent(n: number): string {
  let result = '';
  while (n-- > 0) result += '  ';
  return result;
}
